import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { userApi, teamApi } from '../utils/api';
import {
  Users, UserPlus, Pencil, Trash2, Loader, Check, X,
  Building2, ChevronDown, Search, Wallet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ROLES = ['employee', 'manager', 'admin'];

const defaultUser = { email: '', password: '', first_name: '', last_name: '', role: 'employee', team_id: '', manager_id: '', flex_office_code: '', flex_office_desc: '' };
const defaultBalance = { vacation_total: 24, remote_total: 6, carryover_days: 0 };

export default function UserManagement() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const [formData, setFormData] = useState(defaultUser);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Balance dialog
  const [balanceDialog, setBalanceDialog] = useState(null);
  const [balanceForm, setBalanceForm] = useState(defaultBalance);

  // Teams form
  const [teamDialog, setTeamDialog] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', manager_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, teamsRes] = await Promise.all([userApi.list(), teamApi.list()]);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setFormData(defaultUser); setError(''); setDialog('create'); };
  const openEdit = (u) => { setFormData({ ...u, password: '' }); setError(''); setDialog(`edit-${u.id}`); };

  const managersAndAdmins = useMemo(
    () => users.filter(u => u.role === 'manager' || u.role === 'admin'),
    [users]
  );

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (dialog === 'create') {
        await userApi.create({ ...formData, team_id: formData.team_id || undefined, manager_id: formData.manager_id || undefined });
      } else {
        const uid = dialog.replace('edit-', '');
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await userApi.update(uid, { ...payload, team_id: payload.team_id || null, manager_id: payload.manager_id || null });
      }
      await loadData();
      setDialog(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este usuario?')) return;
    try {
      await userApi.delete(id);
      await loadData();
    } catch (e) { console.error(e); }
  };

  const handleTeamSave = async () => {
    setSaving(true);
    try {
      if (teamDialog === 'create') {
        await teamApi.create({ name: teamForm.name, manager_id: teamForm.manager_id || null });
      } else {
        await teamApi.update(teamDialog.replace('edit-', ''), { name: teamForm.name, manager_id: teamForm.manager_id || null });
      }
      await loadData();
      setTeamDialog(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const openBalance = async (u) => {
    try {
      const res = await userApi.getBalance(u.id);
      const b = res.data;
      setBalanceForm({
        vacation_total: b.vacation_total || 24,
        remote_total: b.remote_total || 6,
        carryover_days: b.carryover_days || 0
      });
      setBalanceDialog(u.id);
    } catch (e) { console.error(e); }
  };

  const handleBalanceSave = async () => {
    setSaving(true);
    try {
      await userApi.updateBalance(balanceDialog, {
        vacation_total: parseFloat(balanceForm.vacation_total),
        remote_total: parseFloat(balanceForm.remote_total),
        carryover_days: parseFloat(balanceForm.carryover_days)
      });
      setBalanceDialog(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const filteredUsers = users.filter(u =>
    search === '' ||
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const roleColor = (role) => ({
    admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    employee: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  }[role] || '');

  if (loading) return <div className="flex items-center justify-center h-64"><Loader className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-heading">{t('users')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{users.filter(u => u.is_active).length} usuarios activos</p>
        </div>
        <Button onClick={openCreate} data-testid="btn-create-user" className="bg-brand hover:bg-brand-hover text-white gap-2">
          <UserPlus className="h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="h-3.5 w-3.5 mr-1.5" />Usuarios ({users.length})
          </TabsTrigger>
          <TabsTrigger value="teams" data-testid="tab-teams">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />Equipos ({teams.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4 mt-4">
          <Input
            placeholder="Buscar usuario..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="search-users"
            className="max-w-xs"
          />
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-5 gap-4 px-4 py-2.5 bg-muted/50 border-b border-border text-xs font-semibold text-muted-foreground">
              <span className="col-span-2">NOMBRE</span>
              <span>ROL</span>
              <span>FLEX</span>
              <span className="text-right">ACCIONES</span>
            </div>
            <div className="divide-y divide-border">
              {filteredUsers.map(u => (
                <div key={u.id} className={`grid grid-cols-5 gap-4 px-4 py-3 items-center hover:bg-muted/30 transition-colors ${!u.is_active ? 'opacity-50' : ''}`} data-testid={`user-row-${u.id}`}>
                  <div className="col-span-2 flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {u.first_name?.[0]}{u.last_name?.[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                  <div><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor(u.role)}`}>{t(u.role)}</span></div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                    {u.flex_office_code
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 font-medium" title={u.flex_office_desc || ''}>
                          <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />Tel
                        </span>
                      : <span className="text-muted-foreground/50">—</span>
                    }
                  </div>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openBalance(u)} data-testid={`balance-user-${u.id}`} className="h-7 w-7 p-0 text-brand hover:bg-brand/10" title="Gestionar saldo">
                      <Wallet className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="h-7 w-7 p-0">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {u.is_active && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`} className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="teams" className="space-y-4 mt-4">
          <Button onClick={() => { setTeamForm({ name: '', manager_id: '' }); setTeamDialog('create'); }} className="gap-2 bg-brand hover:bg-brand-hover text-white" data-testid="btn-create-team">
            <Building2 className="h-4 w-4" />Nuevo equipo
          </Button>
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            <div className="divide-y divide-border">
              {teams.map(team => (
                <div key={team.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors" data-testid={`team-row-${team.id}`}>
                  <div>
                    <p className="text-sm font-medium">{team.name}</p>
                    <p className="text-xs text-muted-foreground">{team.manager_name || 'Sin manager'} · {team.member_count || 0} miembros</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setTeamForm({ name: team.name, manager_id: team.manager_id || '' }); setTeamDialog(`edit-${team.id}`); }} className="h-7 w-7 p-0">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={async () => { if (window.confirm('¿Eliminar equipo?')) { await teamApi.delete(team.id); loadData(); } }} className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {teams.length === 0 && (
                <div className="py-8 text-center text-muted-foreground text-sm">No hay equipos creados</div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* User dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog === 'create' ? 'Nuevo usuario' : 'Editar usuario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nombre *</Label>
                <Input value={formData.first_name} onChange={e => setFormData(f => ({ ...f, first_name: e.target.value }))} data-testid="user-first-name" />
              </div>
              <div className="space-y-1">
                <Label>Apellidos *</Label>
                <Input value={formData.last_name} onChange={e => setFormData(f => ({ ...f, last_name: e.target.value }))} data-testid="user-last-name" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} data-testid="user-email" />
            </div>
            <div className="space-y-1">
              <Label>{dialog === 'create' ? 'Contraseña *' : 'Nueva contraseña (dejar vacío para no cambiar)'}</Label>
              <Input type="password" value={formData.password} onChange={e => setFormData(f => ({ ...f, password: e.target.value }))} data-testid="user-password" />
            </div>
            <div className="space-y-1">
              <Label>Rol *</Label>
              <Select value={formData.role} onValueChange={v => setFormData(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Equipo</Label>
              <Select value={formData.team_id || 'none'} onValueChange={v => setFormData(f => ({ ...f, team_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="user-team-select">
                  <SelectValue placeholder="Sin equipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin equipo</SelectItem>
                  {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Manager / Aprobador</Label>
              <Select value={formData.manager_id || 'none'} onValueChange={v => setFormData(f => ({ ...f, manager_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="user-manager-select">
                  <SelectValue placeholder="Sin manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin manager</SelectItem>
                  {managersAndAdmins.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-border pt-3 mt-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Modalidad Flex Office</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Código Flex Office</Label>
                  <Input
                    placeholder="Ej: TT, FW"
                    value={formData.flex_office_code || ''}
                    onChange={e => setFormData(f => ({ ...f, flex_office_code: e.target.value.toUpperCase() }))}
                    data-testid="user-flex-office-code"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Descripción</Label>
                  <Input
                    placeholder="Ej: FlexOffice"
                    value={formData.flex_office_desc || ''}
                    onChange={e => setFormData(f => ({ ...f, flex_office_desc: e.target.value }))}
                    data-testid="user-flex-office-desc"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Con valor: el empleado podrá informar teletrabajo. Vacío = sin teletrabajo (redirige a Trébol).
              </p>
            </div>
            {error && <div className="text-sm text-destructive p-2 bg-destructive/10 rounded">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn" className="bg-brand hover:bg-brand-hover text-white">
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team dialog */}
      <Dialog open={!!teamDialog} onOpenChange={() => setTeamDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{teamDialog === 'create' ? 'Nuevo equipo' : 'Editar equipo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Nombre del equipo *</Label>
              <Input value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} data-testid="team-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Manager responsable</Label>
              <Select value={teamForm.manager_id || 'none'} onValueChange={v => setTeamForm(f => ({ ...f, manager_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="team-manager-select">
                  <SelectValue placeholder="Sin manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin manager</SelectItem>
                  {managersAndAdmins.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamDialog(null)}>Cancelar</Button>
            <Button onClick={handleTeamSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="save-team-btn">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance dialog */}
      <Dialog open={!!balanceDialog} onOpenChange={() => setBalanceDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-brand" />
              Gestionar Saldo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground bg-muted/50 p-2.5 rounded-lg">
              Modifica los días disponibles para el empleado. Los días usados y pendientes no se modifican aquí.
            </p>
            <div className="space-y-1">
              <Label>Días de vacaciones (total año)</Label>
              <Input
                type="number"
                min="0"
                max="60"
                step="0.5"
                value={balanceForm.vacation_total}
                onChange={e => setBalanceForm(f => ({ ...f, vacation_total: e.target.value }))}
                data-testid="balance-vacation-total"
              />
            </div>
            <div className="space-y-1">
              <Label>Días de teletrabajo (total año)</Label>
              <Input
                type="number"
                min="0"
                max="30"
                step="0.5"
                value={balanceForm.remote_total}
                onChange={e => setBalanceForm(f => ({ ...f, remote_total: e.target.value }))}
                data-testid="balance-remote-total"
              />
            </div>
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5">
                Días arrastrados del año anterior
                <span className="text-xs text-muted-foreground font-normal">(usables hasta 30/04)</span>
              </Label>
              <Input
                type="number"
                min="0"
                max="30"
                step="0.5"
                value={balanceForm.carryover_days}
                onChange={e => setBalanceForm(f => ({ ...f, carryover_days: e.target.value }))}
                data-testid="balance-carryover"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalanceDialog(null)}>Cancelar</Button>
            <Button onClick={handleBalanceSave} disabled={saving} data-testid="save-balance-btn" className="bg-brand hover:bg-brand-hover text-white">
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
              Guardar saldo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
